import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { DatePicker, Select, message } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import MessageComposer from '../../Routes/Configuration/Events/components/MessageComposer';
import MessagePreview from './MessagePreview';
import { generatePresignedUrl, uploadToS3 } from '../../../services/attachments';
import LargeAudienceConfirmModal from './LargeAudienceConfirmModal';
import { TIMEZONES, DEFAULT_TEMPLATE_BUTTONS, defaultTimezone } from './constants';
import './MassMessageForm.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

let attachmentIdCounter = 0;

const MassMessageForm = forwardRef(
  (
    {
      audienceCount = 0,
      audienceSection,
      templateButtons,
      enableAttachments = false,
      editingMessage,
      onSubmit,
      isSaving = false,
      renderActions,
      showCostAlways = false,
    },
    ref,
  ) => {
    const [messageText, setMessageText] = useState('');
    const [scheduledDate, setScheduledDate] = useState(null);
    const [tz, setTz] = useState(defaultTimezone);
    const [attachments, setAttachments] = useState([]);
    const [showLargeAudienceConfirm, setShowLargeAudienceConfirm] = useState(false);
    const [pendingFormData, setPendingFormData] = useState(null);

    // Populate fields on edit
    useEffect(() => {
      if (editingMessage) {
        setMessageText(editingMessage.message || '');
        setTz(editingMessage.timezone || defaultTimezone);
        if (editingMessage.scheduledDateTime) {
          setScheduledDate(dayjs(editingMessage.scheduledDateTime).tz(editingMessage.timezone || defaultTimezone));
        }
      }
    }, [editingMessage]);

    // Character count
    const charCount = useMemo(() => {
      const len = (messageText || '').length;
      let color = '#818181';
      if (len >= 600) color = '#FF7A7A';
      else if (len >= 300) color = '#FC8333';
      return { len, color };
    }, [messageText]);

    const estimatedCost = (audienceCount * 0.07).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Attachment handling
    const handleAttach = useCallback(async files => {
      for (const file of files) {
        attachmentIdCounter += 1;
        const tempId = `temp-${attachmentIdCounter}`;
        const localUrl = URL.createObjectURL(file);

        setAttachments(prev => [...prev, { id: tempId, url: localUrl, uploading: true }]);

        try {
          const { presignedUrl, fileUrl } = await generatePresignedUrl({ fileType: file.type });

          if (!presignedUrl) {
            throw new Error('Failed to get presigned URL');
          }

          await uploadToS3({ presignedUrl, file });

          setAttachments(prev =>
            prev.map(att => (att.id === tempId ? { ...att, url: fileUrl, uploading: false } : att)),
          );

          URL.revokeObjectURL(localUrl);
        } catch (err) {
          console.error('Upload failed:', err);
          message.error('Failed to upload image. Please try again.');
          setAttachments(prev => prev.filter(att => att.id !== tempId));
          URL.revokeObjectURL(localUrl);
        }
      }
    }, []);

    const handleRemoveAttachment = useCallback(id => {
      setAttachments(prev => prev.filter(att => att.id !== id));
    }, []);

    const attachmentsForComposer = useMemo(
      () =>
        attachments.map(att => ({
          ...att,
          onRemove: () => handleRemoveAttachment(att.id),
        })),
      [attachments, handleRemoveAttachment],
    );

    // Validation + submit
    const handleSubmit = useCallback(async () => {
      if (!messageText.trim()) {
        message.error('Please enter a message');
        return;
      }
      if (!scheduledDate) {
        message.error('Please select a date and time');
        return;
      }

      const isUploading = attachments.some(att => att.uploading);
      if (isUploading) {
        message.warning('Please wait for images to finish uploading');
        return;
      }

      const formData = {
        message: messageText,
        scheduledDateTime: scheduledDate.toISOString(),
        timezone: tz,
      };

      const attachmentUrls = attachments.map(att => att.url).filter(Boolean);
      if (attachmentUrls.length > 0) {
        formData.attachmentUrls = attachmentUrls;
      }

      setPendingFormData(formData);
      setShowLargeAudienceConfirm(true);
    }, [messageText, scheduledDate, tz, attachments, audienceCount, onSubmit]);

    const handleLargeAudienceConfirm = useCallback(async () => {
      setShowLargeAudienceConfirm(false);
      if (pendingFormData) {
        await onSubmit(pendingFormData);
        setPendingFormData(null);
      }
    }, [pendingFormData, onSubmit]);

    const handleLargeAudienceCancel = useCallback(() => {
      setShowLargeAudienceConfirm(false);
      setPendingFormData(null);
    }, []);

    // Expose submit via ref
    useImperativeHandle(ref, () => ({ submit: handleSubmit }), [handleSubmit]);

    const buttons = templateButtons || DEFAULT_TEMPLATE_BUTTONS;
    const showCost = showCostAlways || audienceCount > 0;

    return (
      <div className="mass-message-form">
        {/* Render actions slot (modal header) */}
        {renderActions?.(handleSubmit, isSaving)}

        {/* Cost estimate */}
        {showCost && (
          <div className="mass-message-form__cost">This could cost ${estimatedCost} if you exceed your plan limit</div>
        )}

        {/* Audience section slot */}
        {audienceSection}

        {/* DELIVERY SCHEDULE */}
        <div className="mass-message-form__section">
          <h3 className="mass-message-form__section-title">
            <FontAwesomeIcon icon={['far', 'calendar-alt']} style={{ marginRight: 8 }} />
            DELIVERY SCHEDULE
          </h3>
          <p className="mass-message-form__tcpa-note">
            Messages only send between 9:00am and 7:00pm in your selected time zone. If a batch is queued outside that
            window, it will send the next day. This helps maintain TCPA compliance.
          </p>
          <div className="mass-message-form__schedule-row">
            <DatePicker
              showTime={{ format: 'hh:mm A', use12Hours: true }}
              format="MMM D, YYYY hh:mm A"
              value={scheduledDate}
              onChange={setScheduledDate}
              style={{ width: 220 }}
              placeholder="Select date and time"
            />
            <Select
              value={tz}
              onChange={setTz}
              options={TIMEZONES}
              style={{ width: 240 }}
              showSearch
              optionFilterProp="label"
              placeholder="Select a Timezone"
            />
          </div>
        </div>

        {/* MESSAGE */}
        <div className="mass-message-form__section">
          <h3 className="mass-message-form__section-title">
            <FontAwesomeIcon icon={['far', 'comment-alt-lines']} style={{ marginRight: 8 }} />
            MESSAGE
          </h3>
          <div className="mass-message-form__message-layout">
            <div className="mass-message-form__composer">
              <MessageComposer
                value={messageText}
                onChange={setMessageText}
                templateButtons={buttons}
                onAttach={enableAttachments ? handleAttach : undefined}
                attachments={enableAttachments ? attachmentsForComposer : undefined}
                footer={
                  <div className="mass-message-form__char-count" style={{ color: charCount.color }}>
                    {charCount.len} characters
                  </div>
                }
              />
            </div>
            <MessagePreview messageText={messageText} attachments={attachments} />
          </div>
          <div className="mass-message-form__guidelines">
            <div className="mass-message-form__guidelines-header">
              <FontAwesomeIcon icon={['far', 'lightbulb']} />
              Message Guidelines
            </div>
            <ul>
              <li>
                We recommend you keep mass messages under <strong>600 characters</strong> for optimal results.
              </li>
              <li>
                The maximum length of a message can be up to <strong>1,500 characters</strong>.
              </li>
              <li>
                If you include emojis, some carriers will not deliver messages over <strong>670 characters</strong>.
              </li>
              <li>
                You can add <strong>PNG, JPG, or GIF</strong> images.
              </li>
              <li>
                For the best phone display, use <strong>640&times;1138</strong> or <strong>1080&times;1920</strong>.
              </li>
              <li>
                The total size of all images together needs to be under <strong>1MB</strong> or they may not be
                delivered by some carriers.
              </li>
              <li>
                Any single image over <strong>600KB</strong> may not be delivered by some carriers.
              </li>
            </ul>
          </div>
        </div>
        <LargeAudienceConfirmModal
          open={showLargeAudienceConfirm}
          onCancel={handleLargeAudienceCancel}
          onConfirm={handleLargeAudienceConfirm}
          audienceCount={audienceCount}
          estimatedCost={estimatedCost}
        />
      </div>
    );
  },
);

MassMessageForm.displayName = 'MassMessageForm';

export default MassMessageForm;
